<template>
    <div class="coverage-map">
        <div class="coverage-map__panel">
            <div class="coverage-map__panel__header">{{ $t('detail.map') }}
                <div class="coverage-env">
                    <label for="coverage-selection">{{ $t('sigfox_coverage.coverage_env') }}:</label>
                    <select name="coverage-selection" v-model="coverageEnv" id="coverageEnv" class="coverage-select" @change="checkCoverage">
                        <option v-for="(value, index) of coverageEnvs" v-bind:key="index" :value="index">{{ value }}</option>
                    </select>
                </div>
            </div>
            <div class="coverage-map__panel__content">
                <transition name="fade">
                    <div v-if="loading" class="loading-wrapper">
                        <BaseSpinner text="info.loading_data" />
                    </div>
                </transition>
                <MglMap
                        class="coverage-map__panel__content__canvas"
                        :trackResize="true"
                        :attributionControl="false"
                        :accessToken="accessToken"
                        :mapStyle="mapStyle"
                        :center="this.coordinates"
                        :maxZoom="maxZoom"
                        :minZoom="minZoom"
                        :zoom="zoom"
                        @load="mapLoaded">
                    <MglFullscreenControl position="top-right"></MglFullscreenControl>
                    <MglNavigationControl position="top-right"></MglNavigationControl>
                </MglMap>
            </div>
        </div>
    </div>
</template>

<script>
    import {mapGetters} from 'vuex';
    import 'mapbox-gl/dist/mapbox-gl.css';
    import 'vue-mapbox/dist/vue-mapbox.css';
    import MglGeocoderControl from '@mapbox/mapbox-gl-geocoder';
    import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
    import Mapbox from 'mapbox-gl';
    import {
        MglMap,
        MglMarker,
        MglPopup,
        MglNavigationControl,
        MglFullscreenControl
    } from 'vue-mapbox';

    export default {
        name: 'CoverageMap',
        props: {
            unit: {
                type: Object,
            }
        },
        components: {
            MglMap,
            MglMarker,
            MglPopup,
            MglNavigationControl,
            MglFullscreenControl
        },
        data() {
            return {
                lng: 0,
                lat: 0,
                loading: false,
                maxZoom: 20,
                minZoom: 1,
                zoom: 4,
                accessToken: process.env.VUE_APP_MAPBOX_KEY,
                mapStyle: 'mapbox://styles/mapbox/streets-v11', // your map style
                coverageEnv: 10,
                coverageEnvs: {
                    10: this.$t('sigfox_coverage.coverage_env_greenhouse'),
                    15: this.$t('sigfox_coverage.coverage_env_indoors'),
                    0: this.$t('sigfox_coverage.coverage_env_outdoors')
                },
                coverageSettings : {
                    15 : {
                        'name': 'indoors',
                        'radius': 1000,
                        'reduction': 0
                    },
                    10 : {
                        'name': 'greenhouse',
                        'radius': 3000,
                        'reduction': 1
                    },
                    0 : {
                        'name': 'outdoors',
                        'radius': 10000,
                        'reduction': 2
                    }
                }
            };
        },
        computed: {
            ...mapGetters('tentacle', ['sigfoxCoverage']),
            coordinates() {
                return [4.455399532609533, 51.924542532352774];
            }
        },
        created() {
            // We need to set mapbox-gl library here in order to use it in template
            this.mapbox = Mapbox;
        },
        methods: {
            mapLoaded(e) {
                // in component https://docs.mapbox.com/mapbox-gl-js/example/point-from-geocoder-result/
                this.map = e.map;

                let manualMarker = new Mapbox.Marker({
                    color: '#da444490'
                });

                const self = this;
                this.map.on('click', function (e) {
                    self.lat = e.lngLat.lat;
                    self.lng = e.lngLat.lng;

                    manualMarker.setLngLat([self.lng, self.lat])
                        .addTo(this);

                    self.checkCoverage();
                });

                var geocoder = new MglGeocoderControl({
                    accessToken: this.accessToken,
                    limit: 3,
                    marker: false,
                    mapboxgl: Mapbox,
                });

                this.map.addControl(geocoder);
            },
            async checkCoverage() {
                this.loading = true;

                const envSettings = this.coverageSettings[this.coverageEnv];
                await this.$store.dispatch('tentacle/checkCoverage', { lng: this.lng, lat: this.lat, radius: envSettings.radius });

                const margins = this.$store.getters['tentacle/sigfoxCoverage'];
                const result = this.getMarginPopupResults(margins);
                const heliumHotspots = this.$store.getters['tentacle/heliumCoverage'];
                const helium = await this.getHeliumPopupResults(heliumHotspots);

                new Mapbox.Popup({offset: 25, closeButton: false})
                    .setLngLat([this.lng, this.lat])
                    .setHTML('<div class="map-popup">' +
                        '<h4>' + result + '</h4>' +
                        '<h4>' + helium + '</h4>' +
                        '</div>')
                    .addTo(this.map);

                this.loading = false;
            },
            getMarginPopupResults(margin) {
                if (margin === null) {
                    return '<p class="coverage-none">' + this.$t('sigfox_coverage.coverage_error') + '</p>';
                }

                let margin1 = margin[0] - this.coverageEnv;
                let margin2 = margin[1] - this.coverageEnv;
                let margin3 = margin[2] - this.coverageEnv;
                let result = this.$t('sigfox_coverage.unstable');
                let coloringClass = 'coverage-none';

                if (margin1 > 10 || margin2 > 10 || margin3 > 10) {
                    result = this.$t('sigfox_coverage.possible_open');
                    coloringClass = 'coverage-bad';
                }

                if (margin1 > 10 && margin2 > 10 && margin3 > 10) {
                    result = this.$t('sigfox_coverage.works_open');
                    coloringClass = 'coverage-working';
                }

                if (margin1 > 20 || margin2 > 20 || margin3 > 20) {
                    result = this.$t('sigfox_coverage.possible_indoor');
                    coloringClass = 'coverage-average';
                }

                if (margin1 > 20 && margin2 > 20 && margin3 > 20) {
                    result = this.$t('sigfox_coverage.works_indoor');
                    coloringClass = 'coverage-sufficient';
                }

                if (margin1 > 30 || margin2 > 30 || margin3 > 30) {
                    result = this.$t('sigfox_coverage.possible_underground');
                    coloringClass = 'coverage-good';
                }

                if (margin1 > 30 && margin2 > 30 && margin3 > 30) {
                    result = this.$t('sigfox_coverage.works_underground');
                    coloringClass = 'coverage-best';
                }

                return '<div class="coverage-info"><svg class="coverage-icon icon" width="24" height="24"><use href="/svg-sprite.svg#'+ coloringClass +'"></use></svg><p class="coverage-message ' + coloringClass + '">Sigfox: ' + result + '</p>';
            },
            async getHeliumPopupResults(nearHotSpots) {
                const margin = this.coverageSettings[this.coverageEnv];
                let hotspotNumber = nearHotSpots.length - 6;
                let hotspotsHtml = '';

                if (nearHotSpots.length > 0){
                    try {
                        nearHotSpots.forEach((hotspot, index) => {
                            if (index === 7) {
                                hotspotsHtml = hotspotsHtml + ' <a class="tooltip coverage-hotspot"><span class="tooltiptext"> +' + hotspotNumber + ' Hotspots</span>...</a>'
                                throw 'BreakException';
                            }

                            let number = index + 1;
                            hotspotsHtml = hotspotsHtml + ' <a class="tooltip coverage-hotspot" href="https://app.hotspotty.net/hotspots/' + hotspot.gateway_id + '/rewards" target="_blank"><span class="tooltiptext">' + hotspot.name + '</span>#' + number + '</a>' ;
                        });
                    } catch (e) {
                        if (e !== 'BreakException') throw e;
                    }

                    hotspotsHtml = '<p class="coverage-hotspots ' + coloringClass + '">Hotspots: ' + hotspotsHtml + '</p></div>';
                } else {
                    hotspotsHtml = '';
                }


                let result = this.$t('sigfox_coverage.unstable');
                let coloringClass = 'coverage-none';
                let hotspotsNumberBad = 3 - margin.reduction;
                let hotspotsNumberAvg = 4 - margin.reduction;
                let hotspotsNumberGood = 5 - margin.reduction;

                if (nearHotSpots.length <= hotspotsNumberBad && nearHotSpots.length > 0) {
                    result = this.$t('sigfox_coverage.works_open');
                    coloringClass = 'coverage-bad';
                }

                if (nearHotSpots.length === hotspotsNumberAvg) {
                    result = this.$t('sigfox_coverage.possible_indoor');
                    coloringClass = 'coverage-average';
                }

                if (nearHotSpots.length >= hotspotsNumberGood) {
                    result = this.$t('sigfox_coverage.works_indoor');
                    coloringClass = 'coverage-good';
                }

                return '<div class="coverage-wrapper"><div class="coverage-info"><svg class="coverage-icon icon" width="24" height="24"><use href="/svg-sprite.svg#'+ coloringClass +'"></use></svg><p class="coverage-message ' + coloringClass + '">Helium: ' + result + '</p></div>' +
                    hotspotsHtml;
            }
        },
    };
</script>
<style>
    .coverage-icon {
        margin: auto;
        min-width: 45px;
        height: 36px;
    }

    .coverage-wrapper {
        display: flex;
        flex-direction: column;
    }

    .coverage-message {
        margin-left: 15px;
        margin-bottom: 0px;
    }

    .coverage-hotspots {
        display: flex;
        margin-top: 5px;
        line-height: 25px;
    }

    .coverage-hotspot {
        padding: 0 6px;
        width: 26px;
        height: 25px;
        line-height: 25px;
        border-radius: 7px;
        background-color: whitesmoke;
        color: #363b54;
        margin-left: 5px;
    }

    .tooltip {
        position: relative;
        display: inline-block;
        cursor: pointer;
    }

    .tooltip .tooltiptext {
        visibility: hidden;
        width: max-content;
        background-color: #333;
        color: #fff;
        text-align: center;
        border-radius: 5px;
        padding: 5px 15px;
        position: absolute;
        z-index: 1;
        bottom: 125%;
        left: 50%;
        margin-left: -50%; /* Adjusted to center the tooltip */
        opacity: 0;
        transition: opacity 0.3s;
    }

    .tooltip:hover .tooltiptext {
        visibility: visible;
        opacity: 1;
    }

    .coverage-info {
        display: flex;
    }

    canvas.mapboxgl-canvas {
        outline: none;
        border: 0.5px solid rgba(172, 180, 197, 0.31);
        border-top: none;
        border-radius: 0 0 2rem 2rem;
    }

    .mapboxgl-popup-content {
        padding: 0 20px;
        width: 350px;
    }

    .map-popup > h3 {
        margin-top: -1px;
        margin-bottom: 2px;
    }

    .coverage-best {
        color: #00c05c;
    }

    .coverage-good {
        color: #6fc908;
    }

    .coverage-sufficient {
        color: #aed11e;
    }

    .coverage-average {
        color: #d8d004;
    }

    .coverage-working {
        color: #d59807;
    }

    .coverage-bad {
        color: #cb6e00;
    }

    .coverage-none {
        color: #d40400;
    }
</style>

<style lang="scss" scoped>
    .coverage-map {
        padding-left: 8rem;
        @media only screen and (max-width: 1199px) {
            padding-left: 0;
            width: 90%;
            margin-right: auto;
            margin-left: 5%;
        }

        &__panel {
            margin-bottom: 4rem;
            border-radius: 2rem;
            background-color: #fff;
            box-shadow: 0 0 5px rgba(100, 116, 160, 0.1);
            position: relative;

            .loading-wrapper {
                position: absolute;
                top: 6rem;
                width: 100%;
                height: calc(100% - 6rem);
                background-color: rgba(255, 255, 255, 0.8);
                display: flex;
                align-items: center;
                justify-content: center;
                z-index: 2;
            }

            &__header {
                padding: 0 3rem;
                height: 6rem;
                border-bottom: 0.1rem solid $border-color;
                display: flex;
                align-items: center;
                font-weight: 600;
                font-size: 1.4rem;
                color: $grey;
                justify-content: space-between;

                .coverage-env {
                    font-weight: bold;
                    min-width: 147px;
                    display: inline-block;
                    line-height: 3.3rem;
                    font-size: 1.3rem;
                    color: #363b54;

                    .coverage-select {
                        border-radius: 0.8rem;
                        margin-left: 20px;
                        height: 4.1rem;
                    }
                }
            }

            &__content {
                min-height: 42rem;
                @media only screen and (max-width: 1199px) {
                    min-height: 26rem;
                }

                &__canvas {
                    height: 42rem;
                    border-radius: 0 0 2rem 2rem;
                    overflow: hidden;
                }
            }
        }
    }
</style>
